import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Paper, CircularProgress, Snackbar, Alert,
  IconButton, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PageHeader from '../components/PageHeader';
import DepartmentTeacherSubjects from '../components/DepartmentTeacherSubjects';
import { getDepartments, createDepartment, updateDepartment, deleteDepartment } from '../services/api';

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentDepartment, setCurrentDepartment] = useState({
    department_name: '',
    department_code: ''
  });
  const [selectedId, setSelectedId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const columns = [
    { field: 'department_id', headerName: 'ID', width: 90 },
    { field: 'department_name', headerName: 'Department Name', flex: 1 },
    { field: 'department_code', headerName: 'Department Code', width: 150 },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton 
            color="primary" 
            size="small"
            onClick={() => handleEdit(params.row)}
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            color="error" 
            size="small"
            onClick={() => handleDeleteClick(params.row.department_id)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await getDepartments();
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load departments',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setCurrentDepartment({
      department_name: '',
      department_code: ''
    });
    setSelectedId(null);
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleEdit = (department) => {
    setCurrentDepartment({
      department_name: department.department_name,
      department_code: department.department_code
    });
    setSelectedId(department.department_id);
    setOpenDialog(true);
    setFormErrors({});
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteDepartment(selectedId);
      setDepartments(departments.filter(dept => dept.department_id !== selectedId));
      setSnackbar({
        open: true,
        message: 'Department deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting department:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete department',
        severity: 'error'
      });
    } finally {
      setConfirmDelete(false);
      setSelectedId(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentDepartment({ ...currentDepartment, [name]: value });
    
    // Clear the error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!currentDepartment.department_name.trim()) {
      errors.department_name = 'Department name is required';
    }
    if (!currentDepartment.department_code.trim()) {
      errors.department_code = 'Department code is required';
    }
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (selectedId) {
        // Update existing department
        await updateDepartment(selectedId, currentDepartment);
        setDepartments(departments.map(dept => 
          dept.department_id === selectedId ? { ...dept, ...currentDepartment, department_id: selectedId } : dept
        ));
        setSnackbar({
          open: true,
          message: 'Department updated successfully',
          severity: 'success'
        });
      } else {
        // Create new department
        const response = await createDepartment(currentDepartment);
        const newDepartment = {
          ...currentDepartment,
          department_id: response.data.department_id
        };
        setDepartments([...departments, newDepartment]);
        setSnackbar({
          open: true,
          message: 'Department created successfully',
          severity: 'success'
        });
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving department:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save department',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRowClick = () => {};

  return (
    <Box>
      <PageHeader 
        title="Departments" 
        subtitle="Manage college departments and teacher-subject mappings" 
        action={
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Add Department
          </Button>
        }
      />

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={departments}
            columns={columns}
            getRowId={(row) => row.department_id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            checkboxSelection={false}
            disableSelectionOnClick
            autoHeight
            onRowClick={handleRowClick}
          />
        )}
      </Paper>

      {/* Add/Edit Department Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedId ? 'Edit Department' : 'Add Department'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              name="department_name"
              label="Department Name"
              fullWidth
              margin="normal"
              value={currentDepartment.department_name}
              onChange={handleInputChange}
              error={!!formErrors.department_name}
              helperText={formErrors.department_name}
            />
            <TextField
              name="department_code"
              label="Department Code"
              fullWidth
              margin="normal"
              value={currentDepartment.department_code}
              onChange={handleInputChange}
              error={!!formErrors.department_code}
              helperText={formErrors.department_code}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this department? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Departments;
